import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { useCentralizedMe } from '@crehana/auth';
import { generateNextPaginationCursor } from '@crehana/compass.knowledgehub/utils';

import GET_TODO_LIST_WORKFLOWS from './graphql/get-todo-list-workflows.v5.graphql';
import type {
  GetTODOListWorkflows,
  GetTODOListWorkflowsVariables,
} from './graphql/types/GetTODOListWorkflows';

const DEFAULT_PAGE_SIZE = 10;

export const useGetTodoListWorkflows = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const { me } = useCentralizedMe();

  const generateAfterCursor = (page: number) => {
    return page > 1 ? generateNextPaginationCursor(page - 1, pageSize) : null;
  };

  const { loading, error, data, refetch } = useQuery<
    GetTODOListWorkflows,
    GetTODOListWorkflowsVariables
  >(GET_TODO_LIST_WORKFLOWS, {
    variables: {
      input: {
        centralized_organization_id: me.organization.id.toString(),
        user_id: me.user.id,
        first: pageSize,
        after:
          currentPage > 1
            ? generateNextPaginationCursor(currentPage - 1, pageSize)
            : null,
      },
    },
    context: {
      clientName: 'v5',
    },
    skip: !me.organization?.id,
  });

  const workflowsData = data?.workflow?.get_tasks_by_user;
  const totalCount = workflowsData?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = workflowsData?.page_info?.has_next_page || false;
  const hasPreviousPage = workflowsData?.page_info?.has_previous_page || false;

  const refetchWithPage = async (newPage: number) => {
    await refetch({
      input: {
        centralized_organization_id: me.organization.id.toString(),
        user_id: me.user.id,
        first: pageSize,
        after: generateAfterCursor(newPage),
      },
    });
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);
    await refetchWithPage(newPage);
  };

  const handleNextPage = async () => {
    if (hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      await refetch({
        input: {
          centralized_organization_id: me.organization.id.toString(),
          user_id: me.user.id,
          first: pageSize,
          after: newPage > 1 ? `cursor-${(newPage - 1) * pageSize}` : null,
        },
      });
    }
  };

  const handlePreviousPage = async () => {
    if (hasPreviousPage) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      await refetchWithPage(newPage);
    }
  };

  const handleRefetch = async () => {
    await refetch();
  };

  return {
    loading,
    error,
    refetch: handleRefetch,
    currentPage,
    totalPages,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    workflowsData,
    handlePageChange,
    handleNextPage,
    handlePreviousPage,
  };
};
